/**
 * WorkspaceProvider — the single source of truth for the active workspace.
 *
 * Why this exists: workspace selection used to be derived ad-hoc in several
 * places (localStorage read at render time in the Header, a reconciliation
 * effect in App.js, a separate render gate, per-page localStorage reads). That
 * created a race on refresh where workspace-scoped pages fetched before the
 * active id was resolved, producing the "Workspace ID required" error.
 *
 * Now there is exactly one place that:
 *   1. holds the active workspace id,
 *   2. reconciles it against the user's actual workspace list,
 *   3. persists it to ``localStorage.WORKSPACE_ID`` (kept for the many call
 *      sites that read it via ``getLocalUser`` / directly),
 *   4. exposes ``ready`` so the app shell can wait until selection is resolved.
 *
 * Consumers use ``useWorkspace()``. The id is also mirrored to localStorage so
 * existing code paths keep working unchanged.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';

const STORAGE_KEY = 'WORKSPACE_ID';

const readStored = () => {
  try { return localStorage.getItem(STORAGE_KEY) || ''; }
  catch (_) { return ''; }
};

const writeStored = (id) => {
  try {
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  } catch (_) { /* private mode; ignore */ }
};

const WorkspaceContext = createContext(null);

export const WorkspaceProvider = ({ children }) => {
  // The user payload carries the authoritative list of workspaces the caller
  // can access. Until it arrives we are not ``ready``.
  const payload = useSelector((s) => s.user_getUser.response);
  const userError = useSelector((s) => s.user_getUser.error);

  const workspaces = useMemo(() => payload?.workspaces || [], [payload]);
  const [activeId, setActiveId] = useState(readStored);
  const [reconciled, setReconciled] = useState(false);

  // Reconcile the stored selection against the real workspace list as soon as
  // it loads: keep a valid stored id, otherwise fall back to the first
  // workspace, or clear it entirely when the user has none.
  useEffect(() => {
    if (!payload) return;
    const ids = workspaces.map((w) => w.uid);
    const stored = readStored();
    let next;
    if (workspaces.length === 0) next = '';
    else if (stored && ids.includes(stored)) next = stored;
    else next = ids[0];

    if (next !== stored) writeStored(next);
    setActiveId(next);
    setReconciled(true);
  }, [payload, workspaces]);

  // Switch to a workspace: persist + hard-navigate to the dashboard so every
  // page mounts cleanly against the new workspace. A single, predictable path.
  const switchWorkspace = useCallback((uid) => {
    if (!uid) return;
    writeStored(uid);
    window.location.assign('/');
  }, []);

  const value = useMemo(() => ({
    activeWorkspaceId: activeId,
    activeWorkspace: workspaces.find((w) => w.uid === activeId) || null,
    workspaces,
    // ``ready`` is true once selection is resolved. We also treat a failed
    // user fetch as ready so the global error handler can surface it instead
    // of the shell hanging on a spinner forever.
    ready: reconciled || !!userError,
    switchWorkspace,
  }), [activeId, workspaces, reconciled, userError, switchWorkspace]);

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return ctx;
};

export default WorkspaceContext;
